import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "../../CommonComponent/Button";
import { visuallyHidden } from "@mui/utils";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Loading from "../../../Loading/Loading";
import useHttp from "../../../Hooks/use-http";
import Overtime from "./Overtime";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  IconButton,
  Tooltip,
  Radio,
  Checkbox,
  TextField,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Collapse,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  FileCopy as FileCopyIcon,
} from "@mui/icons-material";
import DefaultImage from "../../../Assets/default_image.jpg";
import { FormatDateTime } from "../../CommonComponent/DateFormat";
import {
  littleRed,
  littleGreen,
  littleYellow,
} from "../../CommonComponent/DefineColors";
import formatDateTime from "../../CommonComponent/timeFormatT";
import CancelShift from "./CancelShift";
import CommonTablePagination from "../../CommonComponent/CommonTablePagination";
import { PAGINATIONBASEVALUE } from "../../../Constant";
import { toast } from "react-toastify";
import { blue } from "@mui/material/colors";

const Records = ({
  isLoading: parentLoading,
  data,
  headers,
  setIsDataFiltered,
  filteredData,
  overtimeUpdateSuccessHandler,
}) => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedRows, setSelectedRows] = useState([]);
  const [loader, setLoader] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [overTimeDetails, setOverTimeDetails] = useState(null);
  const [overtimeAlertShow, setOvertimeAlertShow] = useState(false);
  const [isOvertimeConfirmHandler, setOvertimeConfirmHandler] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Driver Info");
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedPanel, setExpandedPanel] = useState(false);
  const [copiedItemId, setCopiedItemId] = useState(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGINATIONBASEVALUE);
  const [currentEditValue, setCurrentEditValue] = useState("");
  const [editingField, setEditingField] = useState({
    fuelId: null,
    fieldId: null,
  });
  const [statusCount, setStatusCount] = useState({
    pending: 0,
    started: 0,
    ended: 0,
    cancelled: 0,
  });

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleRowCheckboxChange = React.useCallback((item) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(item.shift_id)) {
        newSet.delete(item.shift_id);
      } else {
        newSet.add(item.shift_id);
      }
      return Array.from(newSet);
    });
  }, []);

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      const allIds =
        filteredData
          ?.filter((item) => !item.shift_startedOn && !item.cancelledOn)
          .map((item) => item.shift_id) || [];
      setSelectedRows(allIds);
    }
    setSelectAll(!selectAll);
  };

  const selectableRows =
    filteredData?.filter(
      (item) => !item.shift_startedOn && !item.cancelledOn
    ) || [];
  const allSelectableRowsSelected =
    selectableRows.length > 0 && selectedRows.length === selectableRows.length;

  const handleCancelSelected = () => {
    setShowCancelDialog(true);
  };

  const getSortableValue = (item, column) => {
    switch (column) {
      case "Driver Info":
        return item?.driver_name;
      case "Shift ID":
        return item?.shift_id;
      case "Corp Name":
        return item?.corporateName;
      case "Scheduled Time":
        return item?.shift_startTime;
      case "Actual Time":
        return item?.shift_startedOn || item?.shift_endedOn;
      case "Shift Location":
        return item?.startLocation || item?.endLocation;
      default:
        return "";
    }
  };

  const getStatusValue = (item) => {
    // Check cancelled first - always last (value: 3)
    if (item.cancelledOn != null || item.status?.toLowerCase() === "cancelled") {
      return 3;
    }
    
    // Check ended: both started and ended are present OR status is "ended" (value: 0 - appears first in ascending)
    const hasStartedOn = item.shift_startedOn != null && item.shift_startedOn !== "";
    const hasEndedOn = item.shift_endedOn != null && item.shift_endedOn !== "";
    const statusIsEnded = item.status?.toLowerCase() === "ended";
    
    if ((hasStartedOn && hasEndedOn) || statusIsEnded) {
      return 0;
    }
    
    // Check started: started but not ended OR status is "started" (value: 1)
    const statusIsStarted = item.status?.toLowerCase() === "started";
    if ((hasStartedOn && !hasEndedOn) || statusIsStarted) {
      return 1;
    }
    
    // Default to pending: neither started nor ended OR status is "pending" (value: 2)
    return 2;
  };

  function descendingComparator(a, b, orderBy) {
    let aValue, bValue;

    switch (orderBy) {
      case "Driver Info":
        aValue = a.driver_name?.toLowerCase() || "";
        bValue = b.driver_name?.toLowerCase() || "";
        break;
      case "Shift ID":
        aValue = a.shift_id?.toLowerCase() || "";
        bValue = b.shift_id?.toLowerCase() || "";
        break;
      case "Corp Name":
        aValue = a.corporateName?.toLowerCase() || "";
        bValue = b.corporateName?.toLowerCase() || "";
        break;
      case "Scheduled Time":
        aValue = new Date(a.shift_startTime).getTime();
        bValue = new Date(b.shift_startTime).getTime();
        break;
      case "Actual Time":
        aValue = new Date(a.shift_startedOn || a.shift_endedOn || 0).getTime();
        bValue = new Date(b.shift_startedOn || b.shift_endedOn || 0).getTime();
        break;
      case "Shift Location":
        aValue = (a.startLocation || a.endLocation || "").toLowerCase();
        bValue = (b.startLocation || b.endLocation || "").toLowerCase();
        break;
      case "Status":
        // Always put cancelled items at the end (regardless of sort direction)
        const aCancelled = a.cancelledOn != null || a.status?.toLowerCase() === "cancelled";
        const bCancelled = b.cancelledOn != null || b.status?.toLowerCase() === "cancelled";
        
        if (aCancelled && !bCancelled) return 1; // a is cancelled, b is not - a goes after b (to end)
        if (!aCancelled && bCancelled) return -1; // b is cancelled, a is not - b goes after a (to end)
        if (aCancelled && bCancelled) return 0; // both cancelled - maintain order
        
        // Neither is cancelled, sort by status value (ended=0, started/pending=1)
        const aStatus = getStatusValue(a);
        const bStatus = getStatusValue(b);
        return aStatus - bStatus;
      default:
        aValue = "";
        bValue = "";
    }

    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }

  function getComparator(order, orderBy) {
    // Special handling for Status to ensure cancelled always at end and proper ordering
    if (orderBy === "Status") {
      return (a, b) => {
        // Always put cancelled items at the end (regardless of sort direction)
        const aCancelled = a.cancelledOn != null || a.status?.toLowerCase() === "cancelled";
        const bCancelled = b.cancelledOn != null || b.status?.toLowerCase() === "cancelled";
        
        if (aCancelled && !bCancelled) return 1; // a is cancelled, b is not - a goes after b (to end)
        if (!aCancelled && bCancelled) return -1; // b is cancelled, a is not - b goes after a (to end)
        if (aCancelled && bCancelled) return 0; // both cancelled - maintain order
        
        // Neither is cancelled, sort by status value
        // Values: Ended=0, Started=1, Pending=2
        const aStatus = getStatusValue(a);
        const bStatus = getStatusValue(b);
        
        if (order === "asc") {
          // Ascending: Ended (0) → Started (1) → Pending (2)
          return aStatus - bStatus;
        } else {
          // Descending: Started (1) → Pending (2) → Ended (0)
          // Custom order: Started first, then Pending, then Ended
          const getDescendingOrder = (status) => {
            if (status === 1) return 0; // Started comes first
            if (status === 2) return 1; // Pending comes second
            if (status === 0) return 2; // Ended comes third
            return 3;
          };
          return getDescendingOrder(aStatus) - getDescendingOrder(bStatus);
        }
      };
    }
    
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    if (!array) return [];

    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const visibleRows = React.useMemo(() => {
    return stableSort(filteredData, getComparator(order, orderBy))?.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [order, orderBy, page, rowsPerPage, filteredData]);

  const driverOvertimeApiResponse = (data) => {
    if (data?.Message?.toLowerCase() === "success") {
      overtimeUpdateSuccessHandler({
        type: "success",
        message:
          overTimeDetails.type.toLowerCase() === "accept"
            ? "Overtime accepted Successfully"
            : "Overtime rejected Successfully",
      });
    } else {
      overtimeUpdateSuccessHandler({
        type: "error",
        message: "Some error occured! Please try again later.",
      });
    }
    setOvertimeConfirmHandler(false);
  };

  const { isLoading, sendRequest } = useHttp();

  useEffect(() => {
    if (isOvertimeConfirmHandler)
      sendRequest(
        {
          url: "/api/v1/DriverList/UpdatetDriverOvertime",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            emailID: localStorage.getItem("user"),
            roleID: "1",
            shiftID: overTimeDetails.shiftId,
            overTime: overTimeDetails.overtime,
          },
        },
        driverOvertimeApiResponse
      );
  }, [sendRequest, isOvertimeConfirmHandler]);

  const acceptRejectIconClickHandler = (item, type, overtimeValue) => {
    setOverTimeDetails({
      driverName: item.driver_name,
      shiftId: item.shift_id,
      overtime: overtimeValue,
      type: type,
    });
    setOvertimeAlertShow(true);
  };

  const handleRowClick = (clickedItem) => {
    const isHeaderClick = headers.includes(clickedItem.shift_id);
    if (isHeaderClick) {
      return;
    }
    setExpandedRow((prevExpandedRow) =>
      prevExpandedRow === clickedItem ? null : clickedItem
    );
    setExpandedPanel(true);
  };

  const handleCopyClick = (event, shiftId) => {
    event.stopPropagation();
    navigator.clipboard.writeText(shiftId);
    setCopiedItemId(shiftId);
    setTimeout(() => setCopiedItemId(null), 2000);
  };

  function calculateTimeDifference(startTime, endTime) {
    const startDate = new Date(startTime);
    let endDate;
    if (endTime == null || undefined) {
      endDate = new Date();
    } else {
      endDate = new Date(endTime);
    }

    const diffInMilliseconds = endDate - startDate;
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;

    return `${hours} h : ${minutes} m`;
  }

  function compareTimeDifferences(startTime1, endTime1, startTime2, endTime2) {
    const startDate1 = new Date(startTime1);
    const endDate1 = new Date(endTime1);
    const startDate2 = new Date(startTime2);
    const endDate2 = new Date(endTime2);

    const diff1 = endDate1 - startDate1;
    const diff2 = endDate2 - startDate2;

    return diff2 < diff1 ? "#D83434" : "#44B150";
  }

  useEffect(() => {
    if (filteredData) {
      let pending = 0;
      let started = 0;
      let ended = 0;
      let cancelled = 0;

      filteredData.forEach((item) => {
        if (item.cancelledOn != null) {
          cancelled++;
        } else if (item.shift_startedOn != null && item.shift_endedOn != null) {
          ended++;
        } else if (item.shift_startedOn != null && item.shift_endedOn == null) {
          started++;
        } else if (item.shift_startedOn == null && item.shift_endedOn == null) {
          pending++;
        }
      });

      setStatusCount({
        pending,
        started,
        ended,
        cancelled,
      });
    }
  }, [filteredData]);

  const toggleEditField = (fuelId, fieldId, currentValue, taskID) => {
    setEditingField((prev) =>
      prev.fuelId === fuelId &&
      prev.fieldId === fieldId &&
      prev.taskID === taskID
        ? { fuelId: null, fieldId: null, taskID: null }
        : { fuelId, fieldId, taskID }
    );
    setCurrentEditValue(currentValue || "");
  };
  const parseTaskDetails = (taskDetails) => {
    if (!taskDetails) return [];

    try {
      if (typeof taskDetails === "string" && taskDetails.startsWith('"')) {
        const firstParse = JSON.parse(taskDetails);
        const parsedDetails =
          typeof firstParse === "string" ? JSON.parse(firstParse) : firstParse;
        return Array.isArray(parsedDetails) ? parsedDetails : [parsedDetails];
      }

      const parsedDetails =
        typeof taskDetails === "string" ? JSON.parse(taskDetails) : taskDetails;

      return Array.isArray(parsedDetails) ? parsedDetails : [parsedDetails];
    } catch (error) {
      console.error("Error parsing task details:", error, taskDetails);
      return [];
    }
  };

  useEffect(() => {
    return () => {
      setEditingField({ fuelId: null, fieldId: null, taskID: null });
      setCurrentEditValue("");
    };
  }, []);

  const handleSubmitEdit = async (fuelId, fieldId, taskID) => {
    try {
      setLoader(true);

      const shift = data.find((f) => f.shift_id === fuelId);

      if (!shift) {
        toast.error("Shift not found");
        setLoader(false);
        return;
      }

      const completedTask = shift.completedTask?.find(
        (task) => task.taskID === taskID
      );

      if (!completedTask) {
        toast.error("Task not found");
        setLoader(false);
        return;
      }

      const originalDetails = parseTaskDetails(completedTask.taskDetails);

      const updatedDetails = originalDetails.map((field) => {
        if (field.FieldID === fieldId) {
          return {
            ...field,
            Answer: currentEditValue,
          };
        }
        return field;
      });

      const payload = {
        shiftID: fuelId,
        taskID: taskID,
        taskDetails: JSON.stringify(updatedDetails),
        modifiedBy: localStorage.getItem("user"),
      };

      sendRequest(
        {
          url: "/shiftTaskUpdate",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: payload,
        },
        (response) => {
          toast.success("Field updated successfully");
          setIsDataFiltered(true);
          setEditingField({ fuelId: null, fieldId: null, taskID: null });
          setCurrentEditValue("");
          setLoader(false);
        },
        (error) => {
          console.error("Error updating field:", error);
          toast.error("Error updating field");
          setLoader(false);
        }
      );
    } catch (error) {
      console.error("Error in handleSubmitEdit:", error);
      toast.error("Error updating field");
      setLoader(false);
    }
  };

  const renderField = (field, index, fuelId, taskID) => {
    if (!field) return null;
    const { FieldText, FieldType, ExtraDetails, Answer, FieldID, Required } =
      field;
    const isEditing =
      editingField.fuelId === fuelId &&
      editingField.fieldId === FieldID &&
      editingField.taskID === taskID;

    // Function to handle edit button click and stop propagation
    const handleEditClick = (e, fuelId, fieldId, currentValue, taskID) => {
      e.stopPropagation(); // Stop event from bubbling up to parent elements
      toggleEditField(fuelId, fieldId, currentValue, taskID);
    };

    // Function to handle all edit-related clicks and stop propagation
    const handleEditInteraction = (e) => {
      e.stopPropagation(); // Prevent click from propagating to parent
    };

    // Function to handle text field changes
    const handleTextFieldChange = (e) => {
      e.stopPropagation(); // Stop event propagation
      setCurrentEditValue(e.target.value);
    };

    // Function to handle radio button changes
    const handleRadioChange = (e, valueId) => {
      e.stopPropagation(); // Stop event propagation
      setCurrentEditValue(valueId);
    };

    switch (FieldType) {
      case "INPUT":
      case "NUMBER":
        return (
          <div style={{ marginBottom: "10px" }} onClick={handleEditInteraction}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                {FieldText}{" "}
                {Required && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {!isEditing ? (
                <IconButton
                  size="small"
                  onClick={(e) =>
                    handleEditClick(e, fuelId, FieldID, Answer, taskID)
                  }
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
            {isEditing ? (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                onClick={handleEditInteraction}
              >
                <TextField
                  value={currentEditValue || Answer || ""}
                  onChange={handleTextFieldChange}
                  onClick={handleEditInteraction}
                  onKeyDown={handleEditInteraction}
                  fullWidth
                  variant="outlined"
                  size="small"
                  type={FieldType === "NUMBER" ? "number" : "text"}
                  required={Required}
                  autoFocus
                  // width="auto"
                  maxWidth="150px"
                />
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubmitEdit(fuelId, FieldID, taskID);
                  }}
                  disabled={Required && !currentEditValue}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingField({
                      fuelId: null,
                      fieldId: null,
                      taskID: null,
                    });
                    setCurrentEditValue("");
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <TextField
                value={Answer || ""}
                disabled
                fullWidth
                variant="outlined"
                size="small"
                sx={{ backgroundColor: "#f5f5f5" }}
                onClick={handleEditInteraction}
                maxWidth="150px"
              />
            )}
          </div>
        );

      case "IMAGE":
        return (
          <div style={{ marginBottom: "10px" }} onClick={handleEditInteraction}>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              {FieldText} {Required && <span style={{ color: "red" }}>*</span>}
            </Typography>
            {Answer && Answer !== "null" ? (
              <a href={Answer} target="_blank" rel="noopener noreferrer">
                <img
                  src={Answer}
                  alt="Answer"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </a>
            ) : (
              <a href={DefaultImage} target="_blank" rel="noopener noreferrer">
                <img
                  src={DefaultImage}
                  alt="Default"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </a>
            )}
          </div>
        );

      case "MULTI_CHOICE":
        return (
          <div style={{ marginBottom: "10px" }} onClick={handleEditInteraction}>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              {FieldText} {Required && <span style={{ color: "red" }}>*</span>}
            </Typography>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {ExtraDetails?.FieldValue?.map((value, idx) => (
                <div
                  key={idx}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Checkbox
                    checked={Answer?.includes?.(value.ValueID)}
                    disabled
                    size="small"
                    onClick={handleEditInteraction}
                  />
                  <Typography variant="body2">{value.ValueText}</Typography>
                </div>
              ))}
            </div>
          </div>
        );

      case "SINGLE_CHOICE":
        return (
          <div style={{ marginBottom: "10px" }} onClick={handleEditInteraction}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                {FieldText}{" "}
                {Required && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {!isEditing ? (
                <IconButton
                  size="small"
                  onClick={(e) =>
                    handleEditClick(e, fuelId, FieldID, Answer, taskID)
                  }
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
            {isEditing ? (
              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                onClick={handleEditInteraction}
              >
                <div
                  style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  onClick={handleEditInteraction}
                >
                  {ExtraDetails?.FieldValue?.map((value, idx) => (
                    <div
                      key={idx}
                      style={{ display: "flex", alignItems: "center" }}
                      onClick={handleEditInteraction}
                    >
                      <Radio
                        checked={currentEditValue === value.ValueID}
                        onChange={(e) => handleRadioChange(e, value.ValueID)}
                        onClick={handleEditInteraction}
                        size="small"
                      />
                      <Typography variant="body2">{value.ValueText}</Typography>
                    </div>
                  ))}
                </div>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <IconButton
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubmitEdit(fuelId, FieldID, taskID);
                    }}
                    disabled={Required && !currentEditValue}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingField({
                        fuelId: null,
                        fieldId: null,
                        taskID: null,
                      });
                      setCurrentEditValue("");
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <div
                style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                onClick={handleEditInteraction}
              >
                {ExtraDetails?.FieldValue?.map((value, idx) => (
                  <div
                    key={idx}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Radio
                      checked={Answer === value.ValueID}
                      disabled
                      size="small"
                      onClick={handleEditInteraction}
                    />
                    <Typography variant="body2">{value.ValueText}</Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const MobileCardView = ({ item }) => {
    const getStatusColor = (status) => {
      switch (status?.toLowerCase()) {
        case "pending":
          return littleYellow;
        case "started":
          return littleGreen;
        case "ended":
          return littleRed;
        case "cancelled":
          return littleRed;
        default:
          return "inherit";
      }
    };

    return (
      <Paper
        sx={{
          p: 1,
          mb: 2,
          borderRadius: 2,
          backgroundColor: expandedRows[item.shift_id] ? "#f5f5f5" : "white",
        }}
        onClick={() => toggleRowExpand(item.shift_id)}
      >
        {/* <Box display="flex" justifyContent="" alignItems="flex-start">
          <Box sx={{ flex: "0 0 2%" }}>
            <Checkbox
              checked={selectedRows.includes(item.shift_id || item.cancelledOn)}
              onChange={(e) => {
                e.stopPropagation();
                handleRowCheckboxChange(item);
              }}
            />
          </Box>
          <Box sx={{ flex: "1 1 98%" }}> */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {item.driver_name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {item.car_info?.split(",")[0] || "-"} -{" "}
              {item.car_info?.split(",")[1] || "-"}
            </Typography>
          </Box>
          <IconButton size="small">
            {expandedRows[item.shift_id] ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
          <Typography variant="body2">
            <strong>Shift ID:</strong> {item.shift_id?.substring(0, 9)}...
          </Typography>
          <Chip
            label={item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
            size="small"
            sx={{
              backgroundColor: getStatusColor(item.status),
              color: "white",
              fontSize: "10px",
            }}
          />
        </Box>

        {localStorage.getItem("roleId") == "1" && (
          <Typography variant="body2" mt={1}>
            <strong>Corp:</strong> {item.corporateName}
          </Typography>
        )}

        <Typography variant="caption" display="block" mt={1}>
          <strong>Scheduled:</strong> {item.shift_startTime?.slice(0, 16)} to{" "}
          {item.shift_endTime?.slice(0, 16)}
        </Typography>

        {expandedRows[item.shift_id] && (
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Task Details
            </Typography>
            {item.completedTask?.map((task) => (
              <Paper
                key={task.taskHistoryID}
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: "#fafafa",
                  borderRadius: "8px",
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {task.taskName}
                </Typography>

                <Box
                  display="flex"
                  justifyContent="space-evenly"
                  mb={1}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Chip
                    label={`Required: ${task.isRequired}`}
                    size="small"
                    color={task.isRequired ? "success" : "error"}
                  />
                  <Chip
                    label={`OnStart: ${task.isOnStart}`}
                    size="small"
                    color={task.isOnStart ? "success" : "error"}
                  />
                  <Chip
                    label={`OnEnd: ${task.isOnEnd}`}
                    size="small"
                    color={task.isOnEnd ? "success" : "error"}
                  />
                </Box>

                <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
                  {task.taskDetails &&
                    (() => {
                      try {
                        const parsedDetails = JSON.parse(task.taskDetails);
                        const fields = Array.isArray(parsedDetails)
                          ? parsedDetails
                          : typeof parsedDetails === "string"
                          ? JSON.parse(parsedDetails)
                          : Object.values(parsedDetails);

                        return (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr",
                              gap: 2,
                            }}
                          >
                            {fields.map((field, index) => (
                              <Paper
                                key={index}
                                sx={{
                                  p: 1,
                                  backgroundColor: "white",
                                }}
                              >
                                {renderField(
                                  field,
                                  index,
                                  item.shift_id,
                                  task.taskID
                                )}
                              </Paper>
                            ))}
                          </Box>
                        );
                      } catch (error) {
                        console.error("Error parsing task details:", error);
                        return (
                          <Typography variant="body2" color="error">
                            Error loading task details
                          </Typography>
                        );
                      }
                    })()}
                </Box>

                <Box textAlign="right" mt={1}>
                  <Typography variant="caption">
                    {task?.updatedAt ? "Modified On " : "Created on "}
                    {formatDateTime(task?.updatedAt || task?.createdAt)} at{" "}
                    <a
                      href={`https://www.google.com/maps?q=${task?.location}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {task?.location}
                    </a>
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
        {/* </Box> */}
        {/* </Box> */}
      </Paper>
    );
  };

  return (
    <React.Fragment>
      <Backdrop
        sx={{
          color: "rgba(34, 137, 203, 255)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog
        open={overtimeAlertShow}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Alert"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You are going to {overTimeDetails?.type}{" "}
            {overTimeDetails?.driverName}'s overtime. Are you sure you want to{" "}
            {overTimeDetails?.type}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={() => setOvertimeAlertShow(false)} size="small" />
          <Button
            label="Yes"
            onClick={() => {
              setOvertimeAlertShow(false);
              setOvertimeConfirmHandler(true);
            }}
            autoFocus
            size="small"
          />
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            mb: 2,
            borderRadius: "10px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: "none",
            borderBottom: "none",
          }}
        >
          {selectedRows.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "8px",
              }}
            >
              <span>{selectedRows.length} selected</span>
              <Button
                label="Cancel Selected"
                variant="contained"
                color="error"
                size="small"
                onClick={handleCancelSelected}
              />
            </Box>
          )}

          <CancelShift
            open={showCancelDialog}
            onClose={() => setShowCancelDialog(false)}
            selectedRows={selectedRows}
            isRefresh={setIsDataFiltered}
            setSelectedRows={setSelectedRows}
          />

          {isMobile ? (
            // Mobile View
            <Box sx={{ p: 2 }}>
              {parentLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="200px"
                >
                  <Loading datatable="true" />
                </Box>
              ) : visibleRows?.length === 0 ? (
                <Box textAlign="center" mt={2}>
                  No Data Available
                </Box>
              ) : (
                visibleRows.map((item, index) => (
                  <MobileCardView key={index} item={item} />
                ))
              )}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer 
              component={Paper}
              sx={{
                border: "none",
                borderBottom: "none",
                "& .MuiPaper-root": {
                  border: "none",
                  borderBottom: "none",
                },
              }}
            >
              <Table 
                size="small"
                sx={{
                  "& .MuiTableCell-root": {
                    padding: "4px 8px",
                    lineHeight: "1.3",
                  },
                  "& .MuiTableRow-root": {
                    height: "auto",
                    "& td": {
                      padding: "4px 8px",
                      lineHeight: "1.3",
                    },
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      style={{
                        width:
                          localStorage.getItem("roleId") !== "1"
                            ? "1%"
                            : undefined,
                        padding: "4px 8px",
                      }}
                    >
                      <Checkbox
                        checked={allSelectableRowsSelected}
                        onChange={handleSelectAllChange}
                        indeterminate={
                          selectedRows.length > 0 &&
                          selectedRows.length < selectableRows.length
                        }
                        disabled={selectableRows.length === 0}
                        size="small"
                      />
                    </TableCell>
                    <TableCell
                      padding="none"
                      style={{ width: "1%", padding: "4px" }}
                    ></TableCell>
                    {headers?.map((header) => {
                      if (
                        localStorage.getItem("roleId") !== "1" &&
                        header === "Corporate Name"
                      ) {
                        return null;
                      }
                      return (
                        <TableCell
                          key={header}
                          align={"center"}
                          padding={header.disablePadding ? "none" : "normal"}
                          sortDirection={orderBy == header ? order : false}
                            sx={{
                            py: 0.5,
                            px: localStorage.getItem("roleId") != "1" ? 1.5 : 1,
                            fontWeight: "bold",
                            color: "rgba(0, 0, 0, 0.87)",
                            fontSize: "0.8rem",
                            ...(header === "Shift ID" && {
                              minWidth: "140px",
                              width: "140px",
                            }),
                          }}
                        >
                          <TableSortLabel
                            active={true}
                            direction={orderBy == header ? order : "asc"}
                            onClick={(event) =>
                              handleRequestSort(event, header)
                            }
                              sx={{ fontSize: "0.8rem" }}
                          >
                            {header}
                            {orderBy === header ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows?.length === 0 && !parentLoading && (
                    <TableRow>
                      <TableCell colSpan={headers?.length + 1}>
                        <Box textAlign="center" mt={1}>
                          No Data Available
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {parentLoading ? (
                    <TableRow>
                      <TableCell colSpan={headers?.length + 1}>
                        <Loading datatable="true" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows?.map((item, index) => (
                      <React.Fragment key={index}>
                        <TableRow
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRowClick(item, event);
                          }}
                          style={{ cursor: "pointer" }}
                          sx={{ height: "auto", "& td": { padding: "4px 8px", lineHeight: "1.3" } }}
                        >
                          <TableCell
                            padding="checkbox"
                            style={{
                              padding: "4px 8px",
                            }}
                          >
                            {/* Show checkbox if row is selectable, otherwise show expand icon if available */}
                            {item.shift_startedOn || item.cancelledOn ? (
                              // No checkbox - show expand icon in checkbox position if available
                              item.completedTask?.length > 0 ? (
                                <div
                                  style={{
                                    paddingLeft: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedRow(
                                      expandedRow === item ? null : item
                                    );
                                  }}
                                >
                                  {expandedRow === item ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </div>
                              ) : null
                            ) : (
                              // Show checkbox for selectable rows
                              <Checkbox
                                checked={selectedRows.includes(
                                  item.shift_id || item.cancelledOn
                                )}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleRowCheckboxChange(item);
                                }}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell padding="none" style={{ padding: "4px" }}>
                            {/* Show expand icon in original position only if checkbox is visible */}
                            {!(item.shift_startedOn || item.cancelledOn) &&
                              item.completedTask?.length > 0 && (
                                <div
                                  style={{
                                    paddingLeft: "4px",
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedRow(
                                      expandedRow === item ? null : item
                                    );
                                  }}
                                >
                                  {expandedRow === item ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </div>
                              )}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.5,
                              px: localStorage.getItem("roleId") != "1" ? 1.5 : 1,
                              minWidth: "140px",
                              width: "140px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Tooltip
                                title={
                                  copiedItemId === item.shiftId
                                    ? "Copied!"
                                    : "Copy Shift ID"
                                }
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  onClick={(event) =>
                                    handleCopyClick(event, item.shiftId)
                                  }
                                  sx={{ 
                                    padding: "2px",
                                    "& .MuiSvgIcon-root": {
                                      fontSize: "0.875rem",
                                      color: "#9e9e9e",
                                    },
                                    "&:hover .MuiSvgIcon-root": {
                                      color: "#757575",
                                    },
                                  }}
                                >
                                  <FileCopyIcon />
                                </IconButton>
                              </Tooltip>
                              <span>{item.shiftId.substring(0, 8) + "..."}</span>
                            </Box>
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.5,
                              px: localStorage.getItem("roleId") != "1" ? 1.5 : 1,
                            }}
                          >
                            {item.driver_name}
                            {item.regNumber ? (
                              <div>
                                <span
                                  style={{ color: "gray", fontSize: "9px" }}
                                >
                                  ({item?.model || "-"}-{item?.regNumber || "-"}
                                  )
                                </span>
                                
                              </div>
                            ) : (
                              <div>
                                <span
                                  style={{ color: "gray", fontSize: "9px" }}
                                >
                                  ({item?.car_info.split(",")[0] || "-"}-
                                  {item?.car_info.split(",")[1] || "-"})
                                </span>
                              </div>
                            )}
                          </TableCell>
                          {localStorage.getItem("roleId") == "1" && (
                            <TableCell
                              sx={{
                                fontSize: "0.8rem",
                                py: 0.5,
                                px:
                                  localStorage.getItem("roleId") != "1" ? 1.5 : 1,
                              }}
                            >
                              {item.corporateName}
                            </TableCell>
                          )}

                          <TableCell
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.5,
                              px:
                                localStorage.getItem("roleId") !== "1" ? 1.5 : 1,
                            }}
                          >
                            {item.shift_startTime || item.shift_endTime ? (
                              <>
                                <div style={{ lineHeight: "1.2", fontSize: "0.7rem" }}>{item.shift_startTime.slice(0, 16)}</div>
                                <div style={{ lineHeight: "1.2", fontSize: "0.7rem" }}>to</div>
                                <div style={{ lineHeight: "1.2", fontSize: "0.7rem" }}>{item.shift_endTime.slice(0, 16)}</div>
                                <div style={{ color: "gray", lineHeight: "1.2", fontSize: "0.65rem", marginTop: "2px" }}>
                                  (
                                  {item.shift_startTime && item.shift_endTime
                                    ? calculateTimeDifference(
                                        item.shift_startTime,
                                        item.shift_endTime
                                      )
                                    : ""}
                                  )
                                </div>
                              </>
                            ) : (
                              <div>-</div>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.5,
                              px: localStorage.getItem("roleId") != "1" ? 1.5 : 1,
                            }}
                          >
                            <>
                              {item.shift_startedOn || item.shift_endedOn ? (
                                <>
                                  <div style={{ lineHeight: "1.2", fontSize: "0.7rem" }}>
                                    {FormatDateTime(item.shift_startedOn)}
                                  </div>
                                  <div style={{ lineHeight: "1.2", fontSize: "0.7rem" }}>to</div>
                                  <div style={{ lineHeight: "1.2", fontSize: "0.7rem" }}>
                                    {item.shift_endedOn == null
                                      ? "-"
                                      : FormatDateTime(item.shift_endedOn)}
                                  </div>

                                  <div
                                    style={{
                                      color: compareTimeDifferences(
                                        item.shift_startTime,
                                        item.shift_endTime,
                                        item.shift_startedOn,
                                        item.shift_endedOn
                                      ),
                                      fontWeight: "800",
                                      lineHeight: "1.2",
                                      fontSize: "0.65rem",
                                      marginTop: "2px",
                                    }}
                                  >
                                    (
                                    {item.shift_startedOn && item.shift_endedOn
                                      ? calculateTimeDifference(
                                          item.shift_startedOn,
                                          item.shift_endedOn
                                        )
                                      : ""}
                                    )
                                  </div>
                                </>
                              ) : (
                                <div>-</div>
                              )}
                            </>
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.5,
                              px: localStorage.getItem("roleId") != "1" ? 1.5 : 1,
                            }}
                          >
                            <>
                              {item.startLocation && item.endLocation ? (
                                <>
                                  <div style={{ lineHeight: "1.2", fontSize: "0.7rem", marginBottom: "2px" }}>
                                    <strong>Start -</strong>{" "}
                                    {item.startLocation && (
                                      <a
                                        href={`https://www.google.com/maps?q=${
                                          item.startLL || ""
                                        }`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          textDecoration: "underline",
                                          color: "inherit",
                                          "&:hover": {
                                            color: "blue",
                                          },
                                        }}
                                      >
                                        {(() => {
                                          try {
                                            const parts =
                                              item.startLocation.split(",");
                                            return `${parts[0] || ""}${
                                              parts[1] ? `, ${parts[1]}` : ""
                                            }`;
                                          } catch (e) {
                                            return item.startLocation;
                                          }
                                        })()}
                                      </a>
                                    )}
                                    {item.startOtp && ` - ${item.startOtp}`}
                                  </div>
                                  <div style={{ lineHeight: "1.2", fontSize: "0.7rem" }}>
                                    <strong>End -</strong>{" "}
                                    {item.endLocation && (
                                      <a
                                        href={`https://www.google.com/maps?q=${
                                          item.endLL || ""
                                        }`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          textDecoration: "underline",
                                          color: "inherit",
                                          "&:hover": {
                                            color: "blue",
                                          },
                                        }}
                                      >
                                        {(() => {
                                          try {
                                            const parts =
                                              item.endLocation.split(",");
                                            return `${parts[0] || ""}${
                                              parts[1] ? `, ${parts[1]}` : ""
                                            }`;
                                          } catch (e) {
                                            return item.endLocation;
                                          }
                                        })()}
                                      </a>
                                    )}
                                    {item.endOtp && ` - ${item.endOtp}`}
                                  </div>
                                </>
                              ) : (
                                <div>-</div>
                              )}
                            </>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.5,
                              px: localStorage.getItem("roleId") != "1" ? 1.5 : 1,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {item.taskNames
                              ? item.taskNames
                                  ?.trim()
                                  ?.split(",")
                                  ?.map((task, index) => (
                                    <div key={index} style={{ lineHeight: "1.2", fontSize: "0.7rem", marginBottom: "2px" }}>
                                      {index + 1}. {task.trim()}
                                    </div>
                                  ))
                              : "-"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.8rem",
                              py: 0.5,
                              px: localStorage.getItem("roleId") != "1" ? 1.5 : 1,
                            }}
                          >
                            <div
                              style={{
                                display:
                                  item.status === "cancelled" ? "flex" : "",
                                alignItems:
                                  item.status === "cancelled" ? "center" : "",
                              }}
                            >
                              {item?.status && (
                                <>
                                  <div
                                    style={{
                                      color:
                                        item.status.toLowerCase() === "pending"
                                          ? littleYellow
                                          : item.status.toLowerCase() ===
                                            "started"
                                          ? littleGreen
                                          : item.status.toLowerCase() ===
                                              "ended" ||
                                            item.status.toLowerCase() ===
                                              "cancelled"
                                          ? littleRed
                                          : "inherit",
                                      border: `1px solid ${
                                        item.status.toLowerCase() === "pending"
                                          ? littleYellow
                                          : item.status.toLowerCase() ===
                                            "started"
                                          ? littleGreen
                                          : item.status.toLowerCase() ===
                                              "ended" ||
                                            item.status.toLowerCase() ===
                                              "cancelled"
                                          ? littleRed
                                          : "inherit"
                                      }`,
                                      borderRadius: "10px",
                                      padding: "2px 5px",
                                      fontWeight: "500",
                                      fontSize: "10px",
                                      textAlign: "center",
                                      textTransform: "capitalize",
                                      width: "auto",
                                      margin: "auto 8px",
                                    }}
                                  >
                                    {item.status.charAt(0).toUpperCase() +
                                      item.status.slice(1)}
                                  </div>
                                  {item.status.toLowerCase() ===
                                    "cancelled" && (
                                    <div>
                                      <Tooltip
                                        title={
                                          <div>
                                            <div>
                                              <strong>Cancelled On:</strong>{" "}
                                              {formatDateTime(item.cancelledOn)}
                                            </div>
                                            <div>
                                              <strong>Cancelled By:</strong>{" "}
                                              {item.cancelledBy}
                                            </div>
                                            <div>
                                              <strong>Reason:</strong>{" "}
                                              {item.cancelReason}
                                            </div>
                                          </div>
                                        }
                                      >
                                        <InfoOutlinedIcon
                                          style={{
                                            fontSize: 14,
                                            cursor: "pointer",
                                          }}
                                        />
                                      </Tooltip>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                          {/* <TableCell
                            sx={{
                              fontSize: "0.8rem",
                              p: 1,
                              px: localStorage.getItem("roleId") != "1" ? 2 : 1,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <Overtime
                                item={item}
                                acceptRejectIconClickHandler={
                                  acceptRejectIconClickHandler
                                }
                              />
                            </div>
                          </TableCell> */}
                        </TableRow>

                        {expandedRow === item &&
                          item.completedTask?.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={headers.length}>
                                <div
                                  style={{
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "4px",
                                    padding: "16px",
                                    marginLeft: "5%",
                                    width: "105%",
                                  }}
                                >
                                  <TableContainer
                                    style={{
                                      width: "100%",
                                      fontSize: "0.5rem",
                                    }}
                                  >
                                    <Table
                                      size="small"
                                      aria-label="a dense table"
                                    >
                                      <TableHead>
                                        <TableRow>
                                          <TableCell
                                            sx={{
                                              py: 0,
                                              px: 2,
                                              fontWeight: "bold",
                                              color: "rgba(0, 0, 0, 0.87)",
                                              width: "15%",
                                            }}
                                          >
                                            Name
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              py: 0,
                                              px: 3,
                                              fontWeight: "bold",
                                              color: "rgba(0, 0, 0, 0.87)",
                                              width: "25%",
                                            }}
                                          >
                                            Validations
                                          </TableCell>

                                          <TableCell
                                            sx={{
                                              py: 0,
                                              px: 5,
                                              fontWeight: "bold",
                                              color: "rgba(0, 0, 0, 0.87)",
                                              width: "55%",
                                            }}
                                          >
                                            Fields
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody component={Paper}>
                                        {item?.completedTask?.map((task) => (
                                          <TableRow key={task.taskHistoryID}>
                                            <TableCell>
                                              {task.taskName}
                                            </TableCell>
                                            <TableCell>
                                              <div
                                                style={{
                                                  display: "flex",
                                                  gap: "15px",
                                                }}
                                              >
                                                <div>
                                                  <div>
                                                    <strong>Required</strong>{" "}
                                                  </div>
                                                  <div
                                                    style={{
                                                      color: `${
                                                        task.isRequired !== true
                                                          ? "red"
                                                          : "green"
                                                      }`,
                                                    }}
                                                  >
                                                    {String(task.isRequired)}
                                                  </div>
                                                </div>

                                                <div>
                                                  <div>
                                                    <strong>OnStart</strong>{" "}
                                                  </div>
                                                  <div
                                                    style={{
                                                      color: `${
                                                        task.isOnStart !== true
                                                          ? "red"
                                                          : "green"
                                                      }`,
                                                    }}
                                                  >
                                                    {String(task?.isOnStart)}
                                                  </div>
                                                </div>
                                                <div>
                                                  <div>
                                                    <strong>OnEnd</strong>
                                                  </div>
                                                  <div
                                                    style={{
                                                      color: `${
                                                        task.isOnEnd !== true
                                                          ? "red"
                                                          : "green"
                                                      }`,
                                                    }}
                                                  >
                                                    {String(task?.isOnEnd)}
                                                  </div>
                                                </div>
                                              </div>
                                            </TableCell>

                                            <TableCell>
                                              <div
                                                style={{
                                                  maxHeight: "200px",
                                                  overflowY: "auto",
                                                  padding: "10px",
                                                  border: "1px solid #e0e0e0",
                                                  borderRadius: "4px",
                                                  scrollbarWidth: "thin",
                                                  width: "auto",
                                                }}
                                              >
                                                <ul
                                                  style={{
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                      "repeat(2, 1fr)",
                                                    gap: "20px",
                                                  }}
                                                >
                                                  {task?.taskDetails &&
                                                    (() => {
                                                      try {
                                                        const parsedDetails =
                                                          JSON.parse(
                                                            task.taskDetails
                                                          );
                                                        const fields =
                                                          JSON.parse(
                                                            parsedDetails
                                                          );
                                                        return fields.map(
                                                          (field, index) => (
                                                            <Box
                                                              key={index}
                                                              mb={1}
                                                              onClick={(e) =>
                                                                e.stopPropagation()
                                                              }
                                                            >
                                                              {renderField(
                                                                field,
                                                                index,
                                                                item.shift_id,
                                                                task.taskID
                                                              )}
                                                            </Box>
                                                          )
                                                        );
                                                      } catch (error) {
                                                        console.error(
                                                          "Error parsing task details:",
                                                          error
                                                        );
                                                        return null;
                                                      }
                                                    })()}
                                                </ul>
                                              </div>
                                              <div
                                                style={{
                                                  display: "flex",
                                                  justifyContent: "flex-end",
                                                }}
                                              >
                                                <p style={{ fontSize: "10px" }}>
                                                  Modified On{" "}
                                                  {formatDateTime(
                                                    task?.updatedAt
                                                  )}{" "}
                                                  at{" "}
                                                  <a
                                                    style={{ color: "blue" }}
                                                    href={`https://www.google.com/maps?q=${task?.location}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    {task?.location}
                                                  </a>
                                                </p>
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Status Summary and Pagination */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              background: "white",
              zIndex: 1,
              border: "none !important",
              borderTop: "none !important",
              borderBottom: "none !important",
              "&::before": {
                display: "none",
              },
              "&::after": {
                display: "none",
              },
            }}
          >
            <Box
              display="flex"
              sx={{
                paddingRight: "16px",
                marginTop: "8px",
                alignContent: "center",
                alignItems: "center",
                display: "flex",
                justifyContent: "space-between",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 1 : 0,
                border: "none !important",
                borderTop: "none !important",
                borderBottom: "none !important",
                "&::before": {
                  display: "none",
                },
                "&::after": {
                  display: "none",
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  margin: "initial",
                  marginLeft: isMobile ? 0 : "2%",
                  textAlign: isMobile ? "center" : "left",
                  fontSize: isMobile ? "0.8rem" : "0.875rem",
                }}
              >
                <strong style={{ color: "green" }}>Started: </strong>
                <span>{statusCount?.started}</span> <strong>| </strong>
                <strong style={{ color: "red" }}>Ended: </strong>
                <span>{statusCount?.ended}</span>
                <strong style={{ color: littleYellow }}> | Pending:</strong>
                <span> {statusCount?.pending}</span>
                <strong style={{ color: littleRed }}> | Cancelled:</strong>
                <span> {statusCount?.cancelled}</span>
              </Typography>

              {!isMobile && (
                <CommonTablePagination
                  page={page}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  setPage={setPage}
                  filteredData={filteredData}
                  styling={{
                    display: "flex",
                    justifyContent: "flex-end",
                    border: "none",
                    borderTop: "none",
                    borderBottom: "none",
                    "& .MuiTablePagination-root": {
                      border: "none",
                      borderTop: "none",
                      borderBottom: "none",
                      "&::before": {
                        display: "none",
                      },
                      "&::after": {
                        display: "none",
                      },
                    },
                    "& .MuiTablePagination-toolbar": {
                      border: "none",
                      borderTop: "none",
                      borderBottom: "none",
                      "&::before": {
                        display: "none",
                      },
                      "&::after": {
                        display: "none",
                      },
                    },
                    "& .MuiTablePagination-spacer": {
                      border: "none",
                    },
                  }}
                />
              )}
            </Box>
            {isMobile && (
              <Box 
                display="flex" 
                justifyContent="center" 
                pb={2}
                sx={{ border: "none", borderTop: "none", borderBottom: "none" }}
              >
                <CommonTablePagination
                  page={page}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  setPage={setPage}
                  filteredData={filteredData}
                  styling={{
                    border: "none",
                    borderTop: "none",
                    borderBottom: "none",
                    "& .MuiTablePagination-root": {
                      border: "none",
                      borderTop: "none",
                      borderBottom: "none",
                      "&::before": {
                        display: "none",
                      },
                      "&::after": {
                        display: "none",
                      },
                    },
                    "& .MuiTablePagination-toolbar": {
                      border: "none",
                      borderTop: "none",
                      borderBottom: "none",
                      "&::before": {
                        display: "none",
                      },
                      "&::after": {
                        display: "none",
                      },
                    },
                    "& .MuiTablePagination-spacer": {
                      border: "none",
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </React.Fragment>
  );
};

export default Records;
